// ============ BUYER FIRM PANELS ============
// Everything Atlas knows about one buyer firm, as panels the COMPANY page
// mounts when the company is on the buyer roster (roadmap item 5, then merged
// into the company page 202609232300 — a firm is one record, not two pages):
// what they say they buy (editable — a person's band beats a scraped one),
// what they own, who to call, the deals we have sent them, what they said no
// to, what moved on their site, their press, and a "Send a deal" that puts
// them on a seller deal's blast tracker.
//
// The drawer bodies (Passes / Team / Portfolio) are shared with the roster
// via window.Buyer*Body so the two never drift. Everything else here reads
// the same endpoints the Buyers tabs use, filtered to this firm.
// #/buyers/<buyer_id> still parses: BuyerRedirect (bottom) sends it to the
// company page.

const bdMoney = (v) => (v == null ? '—' : v >= 1e6 ? `$${(v / 1e6 % 1 === 0 ? v / 1e6 : (v / 1e6).toFixed(1))}M` : `$${Math.round(v / 1e3)}k`);
const bdDate = (iso) => (iso ? (window.fmtDate ? window.fmtDate(iso) : iso.slice(0, 10)) : '—');

// Section header used by every rail panel — same chrome as the company page.
// bare (the v2 Firm workspace, whose tab is the title and count): the body only.
const BdPanel = ({ icon, title, count, sub, action, children, style, bare = false }) => (bare ? <div style={style}>{children}</div> : (
  <div style={style}>
    <div style={{ padding: '12px 14px 8px', borderBottom: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {icon}
        <span style={{ fontSize: 13, fontWeight: 600 }}>{title}</span>
        {count != null && <span style={{ marginLeft: 'auto', fontSize: 10.5, color: 'var(--text-4)' }}>{count}</span>}
        {action}
      </div>
      {sub && <div style={{ fontSize: 10.5, color: 'var(--text-4)', marginTop: 2 }}>{sub}</div>}
    </div>
    <div style={{ padding: '4px 14px 10px' }}>{children}</div>
  </div>
));
// Loading / empty states; bare (the v2 Firm workspace, no prose): skeleton rows, a dimmed '—'.
const BdLoading = ({ bare, style }) => (bare && window.SkeletonRows ? <div style={style}><window.SkeletonRows rows={3} /></div>
  : <div style={{ ...style, fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>);
const BdEmpty = ({ bare, style, children }) => <div style={{ ...style, fontSize: 12, color: bare ? 'var(--text-4)' : 'var(--text-3)' }}>{bare ? '—' : children}</div>;

const bdRowLabel = { fontSize: 10.5, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 500, width: 110, flexShrink: 0, paddingTop: 3 };
const BdRow = ({ label, value }) => (
  <div style={{ display: 'flex', gap: 10, padding: '5px 14px', alignItems: 'flex-start', minWidth: 0 }}>
    <div style={bdRowLabel}>{label}</div>
    <div style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: 'var(--text)' }}>{value}</div>
  </div>
);
const BdSection = ({ title }) => (
  <div style={{ padding: '14px 14px 6px', fontSize: 10.5, fontWeight: 600, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.06em', borderTop: '1px solid var(--border)' }}>{title}</div>
);

// Reusable loader: null while loading, [] on failure, the items otherwise.
const useBdList = (fn, deps, pick = (r) => (r && r.items) || []) => {
  const [items, setItems] = React.useState(null);
  const [failed, setFailed] = React.useState(false);
  React.useEffect(() => {
    let alive = true;
    setItems(null); setFailed(false);
    Promise.resolve().then(fn).then((r) => { if (alive) setItems(pick(r)); })
      .catch(() => { if (alive) { setItems([]); setFailed(true); } });
    return () => { alive = false; };
  }, deps);  // eslint-disable-line react-hooks/exhaustive-deps
  return [items, failed];
};

// ── Investment profile (editable) ────────────────────────────────────────────
const BUYER_TYPE_OPTIONS = [
  { value: 'preferred_buyer', label: 'Preferred buyer', hue: 'violet' },
  { value: 'peg', label: 'PEG', hue: 'blue' },
  { value: 'other', label: 'Other', hue: 'slate' },
];
const AGREEMENT_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'pb', label: 'Preferred Buyer agreement', hue: 'violet' },
  { value: 'umbrella', label: 'Umbrella agreement', hue: 'teal' },
];
const splitList = (s) => String(s || '').split(/[,\n;]+/).map((x) => x.trim()).filter(Boolean);

// ── Read from a file (app/buyer_intake.py) ──────────────────────────────────
// A file filed on the firm — the one-pager they sent — read into the profile.
// The read runs server-side; document_job says when it is done, so this polls
// the buyer (uncached) until it is. What the read found stays on the buyer as
// document_read, each value with the words in the file it came from.
const BD_DOC_LABEL = {
  preferred_industries: 'Industries', geo_focus: 'Geography', investment_criteria: 'Criteria',
  platform_add_on: 'Deal type', firm_kind: 'Kind', fund_size_text: 'Fund / AUM',
  min_ebitda: 'EBITDA min', max_ebitda: 'EBITDA max', min_revenue: 'Revenue min', max_revenue: 'Revenue max',
  check_size_min: 'Check min', check_size_max: 'Check max',
};
const BD_POLL_MS = 2500;
const BD_POLL_MAX = 72;  // ~3 minutes

const BuyerDocumentRead = ({ buyer, onChange }) => {
  const Btn = window.Btn;
  const job = buyer.document_job;
  const doc = buyer.document_read;
  const reading = job && job.state === 'reading';
  const [files, setFiles] = React.useState(null);   // null = closed, [] = none
  const [showQuotes, setShowQuotes] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  // Poll while a read runs — started here, or by the Atlas Inbox.
  React.useEffect(() => {
    if (!reading || !onChange) return undefined;
    let n = 0;
    let alive = true;
    const t = setInterval(() => {
      n += 1;
      window.AtlasApi.getBuyer({ buyer_id: buyer.buyer_id }, { cacheMs: 0 })
        .then((b) => {
          if (!alive || !b) return;
          if (!b.document_job || b.document_job.state !== 'reading' || n >= BD_POLL_MAX) {
            clearInterval(t);
            onChange(b);
            const j = b.document_job || {};
            if (window.pushToast && j.state && j.state !== 'reading') {
              window.pushToast(j.state === 'done'
                ? { type: 'success', title: `Read ${j.file || 'the file'}`, body: (j.written || []).length ? `Filled ${(j.written || []).map((k) => BD_DOC_LABEL[k] || k).join(', ')}` : 'Nothing new for the profile' }
                : { type: 'error', title: `Could not read ${j.file || 'the file'}`, body: j.error });
            }
          }
        })
        .catch(() => {});
    }, BD_POLL_MS);
    return () => { alive = false; clearInterval(t); };
  }, [reading, buyer.buyer_id]);  // eslint-disable-line react-hooks/exhaustive-deps

  const openFiles = async () => {
    if (files) { setFiles(null); return; }
    try {
      const r = await window.AtlasApi.buyerFiles(buyer.buyer_id);
      setFiles((r && r.items) || []);
    } catch (e) {
      window.pushToast && window.pushToast({ type: 'error', title: 'Could not list the files', body: e.message });
    }
  };
  const read = async (f) => {
    setBusy(true);
    try {
      const r = await window.AtlasApi.readBuyerFile(buyer.buyer_id, f.attachment_id);
      setFiles(null);
      if (onChange) onChange({ ...buyer, document_job: r && r.document_job });
    } catch (e) {
      window.pushToast && window.pushToast({ type: 'error', title: 'Could not start the read', body: e.message });
    } finally {
      setBusy(false);
    }
  };

  const quotes = (doc && doc.quotes) || {};
  const quoted = Object.keys(BD_DOC_LABEL).filter((k) => quotes[k]);
  const linkBtn = { background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--accent-text)', fontSize: 11, textDecoration: 'underline', textUnderlineOffset: 2 };
  return (
    <div style={{ padding: '0 14px 12px', fontSize: 11, color: 'var(--text-4)' }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        {reading
          ? <span>Reading {job.file || 'the file'}…</span>
          : <button style={{ ...linkBtn, ...(window.phoneHitHost ? window.phoneHitHost() : null) }} onClick={openFiles}>{window.PhoneHit && <window.PhoneHit />}{files ? 'Close' : 'Read from a file'}</button>}
        {job && job.state === 'failed' && <span style={{ color: (window.HUE && window.HUE.rose && window.HUE.rose.fg) || 'var(--text-3)' }}>{job.file}: {job.error}</span>}
        {job && job.state === 'rejected' && <span>{job.file}: {job.error}</span>}
        {doc && quoted.length > 0 && (
          <button style={linkBtn} onClick={() => setShowQuotes(!showQuotes)}>{showQuotes ? 'Hide quotes' : `Quotes from ${doc.file}`}</button>
        )}
      </div>
      {files && (
        <div style={{ marginTop: 6, border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface-2)' }}>
          {files.length === 0 && <div style={{ padding: '8px 10px' }}>No documents filed on this firm</div>}
          {files.slice(0, 8).map((f) => (
            <div key={f.attachment_id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderTop: '1px solid var(--border)' }}>
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-2)' }} title={f.where || ''}>{f.file}</span>
              <span>{bdDate(f.at)}</span>
              <Btn size="xs" variant="outline" disabled={busy} onClick={() => read(f)}>Read</Btn>
            </div>
          ))}
        </div>
      )}
      {showQuotes && doc && (
        <div style={{ marginTop: 6, display: 'grid', gap: 4 }}>
          {quoted.map((k) => (
            <div key={k}>
              <span style={{ color: 'var(--text-3)' }}>{BD_DOC_LABEL[k]}{(doc.kept || []).includes(k) ? ' (not applied — set by hand or from the workbook)' : ''}: </span>
              <span style={{ color: 'var(--text-2)' }}>{[].concat(quotes[k]).map((q) => `“${q}”`).join(' · ')}</span>
            </div>
          ))}
          {doc.reasoning && <div style={{ color: 'var(--text-3)' }}>{doc.reasoning}</div>}
          <div>Read {bdDate(doc.read_at)}{doc.by ? ` by ${doc.by}` : ''}</div>
        </div>
      )}
    </div>
  );
};

const BuyerProfilePanel = ({ buyer, users, onSave, onChange }) => {
  const EC = window.EditableCell;
  const L = window.BuyerLabels || {};
  const listCell = (key) => (
    <EC type="multiline" value={(buyer[key] || []).join(', ')} placeholder="comma-separated"
      onSave={(v) => onSave({ [key]: splitList(v) })} />
  );
  const src = buyer.icp_source === 'manual' ? 'edited by hand'
    : buyer.icp_source === 'workbook' ? 'from the Buyer Report workbook'
    : buyer.icp_source === 'scrape' ? 'read off their website' + (buyer.profile_read_at ? ` ${L.fmtBuyerAgo ? L.fmtBuyerAgo(buyer.profile_read_at) : ''}` : '')
    : buyer.icp_source === 'discovery' ? 'from web discovery'
    : buyer.icp_source === 'document' ? `from ${(buyer.document_read && buyer.document_read.file) || 'a file they sent'}`
    : 'not stated anywhere yet';
  return (
    <div style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
      <BdSection title="Investment profile" />
      <BdRow label="Type" value={<EC type="select" value={buyer.buyer_type} options={BUYER_TYPE_OPTIONS} onSave={(buyer_type) => onSave({ buyer_type })} />} />
      <BdRow label="Kind" value={buyer.firm_kind ? <window.Pill hue={(L.FIRM_KIND_HUE || {})[buyer.firm_kind] || 'slate'} sz="sm">{(L.FIRM_KIND_LABEL || {})[buyer.firm_kind] || buyer.firm_kind}</window.Pill> : <span style={{ color: 'var(--text-4)' }}>not read</span>} />
      <BdRow label="EBITDA min" value={<EC type="currency" value={buyer.min_ebitda} onSave={(min_ebitda) => onSave({ min_ebitda })} />} />
      <BdRow label="EBITDA max" value={<EC type="currency" value={buyer.max_ebitda} onSave={(max_ebitda) => onSave({ max_ebitda })} />} />
      <BdRow label="Revenue min" value={<EC type="currency" value={buyer.min_revenue} onSave={(min_revenue) => onSave({ min_revenue })} />} />
      <BdRow label="Revenue max" value={<EC type="currency" value={buyer.max_revenue} onSave={(max_revenue) => onSave({ max_revenue })} />} />
      {buyer.fund_size_text && <BdRow label="Fund / AUM" value={buyer.fund_size_text} />}
      <div style={{ padding: '6px 14px 2px' }}>
        <div style={bdRowLabel}>Industries</div>
        <div style={{ fontSize: 12.5, marginTop: 4 }}>{listCell('preferred_industries')}</div>
      </div>
      <div style={{ padding: '6px 14px 2px' }}>
        <div style={bdRowLabel}>Geography</div>
        <div style={{ fontSize: 12.5, marginTop: 4 }}>{listCell('geo_focus')}</div>
      </div>
      <div style={{ padding: '6px 14px 10px' }}>
        <div style={bdRowLabel}>Criteria</div>
        <div style={{ fontSize: 12.5, marginTop: 4 }}>
          <EC type="multiline" value={buyer.investment_criteria || ''} placeholder="What they say they buy, in their words"
            onSave={(investment_criteria) => onSave({ investment_criteria })} />
        </div>
      </div>
      <div style={{ padding: '0 14px 6px', fontSize: 11, color: 'var(--text-4)' }}>
        Profile {src}{buyer.profile_gaps && buyer.profile_gaps.length ? ` · their site does not state: ${buyer.profile_gaps.join(', ')}` : ''}
      </div>
      <BuyerDocumentRead buyer={buyer} onChange={onChange} />

      <BdSection title="Relationship" />
      <BdRow label="Owner" value={<EC type="user" value={buyer.relationship_owner_id || null} users={users} fallbackLabel={buyer.relationship_owner} onSave={(relationship_owner_id) => onSave({ relationship_owner_id })} />} />
      <BdRow label="Primary contact" value={buyer.primary_contact_id
        ? <a href={`#/people/${buyer.primary_contact_id}`} style={{ color: 'var(--accent-text)', textDecoration: 'none' }}>{buyer.primary_contact_name || 'contact'}{buyer.primary_contact_title ? <span style={{ color: 'var(--text-4)' }}> · {buyer.primary_contact_title}</span> : null}</a>
        : <span style={{ color: 'var(--text-4)' }}>none — pick from the team list</span>} />
      <BdRow label="Agreement" value={<EC type="select" value={buyer.agreement_kind || ''} options={AGREEMENT_OPTIONS} onSave={(agreement_kind) => onSave({ agreement_kind: agreement_kind || null })} />} />
      <BdRow label="Signed" value={<EC type="date" value={buyer.pba_sign_date} onSave={(pba_sign_date) => onSave({ pba_sign_date })} />} />
      <BdRow label="Expires" value={<EC type="date" value={buyer.pba_expiration_date} onSave={(pba_expiration_date) => onSave({ pba_expiration_date })} />} />
      <BdRow label="PB blast" value={<EC type="text" value={buyer.pb_blast_status || ''} placeholder="Yes / Expired / …" onSave={(pb_blast_status) => onSave({ pb_blast_status })} />} />
      <BdRow label="In SourceScrub" value={buyer.in_sourcescrub == null ? <span style={{ color: 'var(--text-4)' }}>unknown</span> : buyer.in_sourcescrub ? 'yes' : 'no'} />
      <div style={{ padding: '6px 14px 2px' }}>
        <div style={bdRowLabel}>Roster note</div>
        <div style={{ fontSize: 12.5, marginTop: 4 }}><EC type="multiline" value={buyer.roster_status_note || ''} placeholder="Why they are on or off the blast" onSave={(roster_status_note) => onSave({ roster_status_note })} /></div>
      </div>
      <div style={{ padding: '6px 14px 14px' }}>
        <div style={bdRowLabel}>Notes</div>
        <div style={{ fontSize: 12.5, marginTop: 4 }}><EC type="multiline" value={buyer.notes || ''} placeholder="Anything the team should know before calling" onSave={(notes) => onSave({ notes })} /></div>
      </div>
    </div>
  );
};

// ── Deals we have sent them ──────────────────────────────────────────────────
const bdDealStatus = (d) => (d.interest_level === 'passed' ? 'passed'
  : (d.stage_slug === 'won' || d.stage_slug === 'ss_won') ? 'won' : 'active');
const BD_INTEREST_HUE = { high: 'green', medium: 'amber', low: 'slate', passed: 'rose' };
const BD_NDA_HUE = { signed: 'green', sent: 'amber', redlined: 'amber', declined: 'rose', not_sent: 'slate' };

// bare (the v2 Firm workspace's Deals tab): no icon / title, no empty-state prose.
const BuyerDealsPanel = ({ buyer, onOpenDeal, tick, onSend, bare = false }) => {
  const [items] = useBdList(() => window.AtlasApi.buyerEngagementsForCompany(buyer.company_id), [buyer.company_id, tick]);
  const [tabPick, setTabPick] = React.useState(null);
  const list = items || [];
  const counts = { all: list.length, active: 0, won: 0, passed: 0 };
  list.forEach((d) => { counts[bdDealStatus(d)] += 1; });
  const tab = tabPick || (counts.active > 0 ? 'active' : 'all');
  const shown = tab === 'all' ? list : list.filter((d) => bdDealStatus(d) === tab);
  const touch = !!(window.AtlasDevice && window.AtlasDevice.touch);
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, marginBottom: 14 }}>
      <div style={{ padding: '12px 14px 8px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 6, ...(touch ? { flexWrap: 'wrap', rowGap: 8 } : null) }}>
        {!bare && <window.Icon.Pipeline size={13} color="var(--accent)" />}
        {!bare && <span style={{ fontSize: 13, fontWeight: 600 }}>Deals with us</span>}
        <div style={{ display: 'flex', gap: 2, marginLeft: bare ? 0 : 8 }}>
          {[['active', 'Active', counts.active], ['won', 'Won', counts.won], ['passed', 'Passed', counts.passed], ['all', 'All', counts.all]].map(([k, label, n]) => {
            const on = tab === k;
            return (
              <button key={k} onClick={() => setTabPick(k)} style={{
                padding: '2px 8px', border: 'none', borderRadius: 5, cursor: 'pointer',
                background: on ? 'var(--surface-2)' : 'transparent', fontSize: 11, fontWeight: on ? 600 : 500,
                color: on ? 'var(--text)' : 'var(--text-3)', display: 'inline-flex', alignItems: 'center', gap: 4,
                // Finger-sized on touch.
                ...(touch ? { padding: '6px 10px', minHeight: (window.AtlasDevice && window.AtlasDevice.phone) ? 44 : 34, fontSize: 13 } : null) }}>
                {label}<span style={{ fontSize: 10, color: 'var(--text-4)', fontVariantNumeric: 'tabular-nums' }}>{n}</span>
              </button>
            );
          })}
        </div>
        {onSend && <window.Btn size="xs" variant="outline" style={{ marginLeft: 'auto' }} icon={<window.Icon.Plus size={11} />} onClick={onSend}>Send a deal</window.Btn>}
      </div>
      {items === null && <BdLoading bare={bare} style={{ padding: '10px 14px' }} />}
      {items !== null && !shown.length && (
        <BdEmpty bare={bare} style={{ padding: '10px 14px' }}>
          {list.length ? `No ${tab} deals.` : 'We have not sent this firm a deal yet.'}
        </BdEmpty>
      )}
      {shown.map((d) => {
        const note = d.interest_level === 'passed' ? (d.pass_detail || d.notes || d.pass_reason) : (d.next_action_notes || d.notes);
        return (
          <div key={d.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 14px', borderTop: '1px solid var(--border)', opacity: d.engagement_archived ? 0.6 : 1 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <button onClick={() => onOpenDeal && onOpenDeal(d.engagement_id)}
                  style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12.5, fontWeight: 600, color: 'var(--accent-text)', textAlign: 'left', ...(window.phoneHitHost ? window.phoneHitHost() : null) }}>{window.PhoneHit && <window.PhoneHit />}
                  {d.seller_company_name || d.engagement_name || '—'}
                </button>
                {d.nda_status && d.nda_status !== 'not_sent' && <window.Pill hue={BD_NDA_HUE[d.nda_status] || 'slate'} sz="sm">NDA {d.nda_status}</window.Pill>}
                {d.interest_level && <window.Pill hue={BD_INTEREST_HUE[d.interest_level] || 'slate'} sz="sm">{d.interest_level}</window.Pill>}
                {d.loi_submitted && <window.Pill hue="blue" sz="sm">LOI</window.Pill>}
                {d.bs_call_date && <window.Pill hue="teal" sz="sm">B/S call {bdDate(d.bs_call_date)}</window.Pill>}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-4)', marginTop: 2 }}>
                {[d.stage_name, d.next_action_date && `next action ${bdDate(d.next_action_date)}`].filter(Boolean).join(' · ')}
              </div>
              {note && <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 3, lineHeight: 1.5 }}>{note}</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ── Right rail: signals, acquisitions, site changes ──────────────────────────
const BD_KIND_LABEL = {
  portfolio_exit: 'Exit', portfolio_add: 'New holding', team_join: 'Joined', team_leave: 'Left',
  criteria_change: 'Criteria changed', acquisition: 'Acquired', fund_close: 'Fund close', press: 'In the news',
};
const BD_KIND_HUE = { portfolio_exit: 'violet', portfolio_add: 'green', team_join: 'blue', team_leave: 'amber', criteria_change: 'teal', acquisition: 'green', fund_close: 'violet', press: 'slate' };

const BuyerSignalsPanel = ({ buyer, bare = false }) => {
  const [items] = useBdList(() => window.AtlasApi.buyerIntent({ buyer_id: buyer.buyer_id, status: 'all', days: 365, limit: 40 }), [buyer.buyer_id]);
  const L = window.BuyerLabels || {};
  return (
    <BdPanel bare={bare} icon={<window.Icon.Lightning size={13} color="var(--accent)" />} title="Buyer activity" count={items ? items.length : null}>
      {items === null && <BdLoading bare={bare} />}
      {items !== null && !items.length && <BdEmpty bare={bare}>Nothing in the last year.</BdEmpty>}
      {(items || []).map((s) => (
        <div key={s.id} style={{ padding: '7px 0', borderBottom: '1px solid var(--border)', opacity: s.status === 'dismissed' ? 0.5 : 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <window.Pill hue={BD_KIND_HUE[s.kind] || 'slate'} sz="sm">{BD_KIND_LABEL[s.kind] || s.kind}</window.Pill>
            <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text)' }}>{s.title}</span>
            <span style={{ marginLeft: 'auto', fontSize: 10.5, color: 'var(--text-4)' }}>{s.occurred_at && L.fmtBuyerAgo ? L.fmtBuyerAgo(s.occurred_at) : bdDate(s.occurred_at)}</span>
          </div>
          {s.detail && <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 2, lineHeight: 1.5 }}>{s.detail}</div>}
          {s.url && <a href={s.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: 'var(--accent-text)' }}>source</a>}
        </div>
      ))}
    </BdPanel>
  );
};

const BD_CONF_HUE = { high: 'green', medium: 'amber', low: 'slate' };
const BuyerAcquisitionsPanel = ({ buyer, setCompanyId, bare = false }) => {
  const [items] = useBdList(() => window.AtlasApi.buyerAcquisitions({ buyer_id: buyer.buyer_id, state: 'all', limit: 100 }), [buyer.buyer_id]);
  const list = (items || []).filter((a) => a.decision !== 'rejected');
  return (
    <BdPanel bare={bare} icon={<window.Icon.Diamond size={13} color="var(--accent)" />} title="Acquisitions" count={items ? list.length : null}>
      {items === null && <BdLoading bare={bare} />}
      {items !== null && !list.length && <BdEmpty bare={bare}>None found in Atlas.</BdEmpty>}
      {list.map((a) => (
        <div key={a.id} style={{ padding: '7px 0', borderBottom: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <button onClick={() => setCompanyId && setCompanyId(a.company_id)}
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12.5, fontWeight: 600, color: 'var(--accent-text)', textAlign: 'left', ...(window.phoneHitHost ? window.phoneHitHost() : null) }}>{window.PhoneHit && <window.PhoneHit />}
              {a.company_name}
            </button>
            <window.Pill hue={BD_CONF_HUE[a.confidence] || 'slate'} sz="sm">{a.confidence}</window.Pill>
            {a.decision === 'confirmed' || a.applied_at ? <window.Pill hue="green" sz="sm">confirmed</window.Pill> : <window.Pill hue="amber" sz="sm">to review</window.Pill>}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-4)', marginTop: 2 }}>
            {[a.announced_date ? bdDate(a.announced_date) : a.announced_year, a.price_text || (a.price_usd ? bdMoney(a.price_usd) : null),
              [a.hq_city, a.hq_state].filter(Boolean).join(', ')].filter(Boolean).join(' · ')}
          </div>
        </div>
      ))}
    </BdPanel>
  );
};

const BuyerChangesPanel = ({ buyer, bare = false }) => {
  const [items] = useBdList(() => window.AtlasApi.buyerSiteChanges({ company_id: buyer.company_id, unreviewed_only: false, days: 365, limit: 40 }), [buyer.company_id]);
  const L = window.BuyerLabels || {};
  return (
    <BdPanel bare={bare} icon={<window.Icon.Globe size={13} color="var(--accent)" />} title="Site changes" count={items ? items.length : null}
      sub={buyer.site_last_scraped_at && L.fmtBuyerAgo ? `Website last read ${L.fmtBuyerAgo(buyer.site_last_scraped_at)}` : 'Website never read'}>
      {items === null && <BdLoading bare={bare} />}
      {items !== null && !items.length && <BdEmpty bare={bare}>Nothing moved on their site in the last year.</BdEmpty>}
      {(items || []).map((c) => (
        <div key={c.id} style={{ padding: '7px 0', borderBottom: '1px solid var(--border)', opacity: c.reviewed_at ? 0.6 : 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <window.Pill hue={c.change_type === 'added' ? 'green' : c.change_type === 'removed' ? 'rose' : 'amber'} sz="sm">{c.change_type}</window.Pill>
            <span style={{ fontSize: 12, fontWeight: 500 }}>{c.field}</span>
            <span style={{ marginLeft: 'auto', fontSize: 10.5, color: 'var(--text-4)' }}>{L.fmtBuyerAgo ? L.fmtBuyerAgo(c.detected_at) : bdDate(c.detected_at)}</span>
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 2, lineHeight: 1.5 }}>
            {c.old_value && <span style={{ textDecoration: 'line-through', opacity: 0.7 }}>{String(c.old_value).slice(0, 120)}</span>}
            {c.old_value && c.new_value && ' → '}
            {c.new_value && <span>{String(c.new_value).slice(0, 160)}</span>}
          </div>
        </div>
      ))}
    </BdPanel>
  );
};

// ── Send a deal ──────────────────────────────────────────────────────────────
// zIndex: the v2 company page's page-drawer z (70 over an open pop-out; undefined = the Drawer's own).
const SendDealDrawer = ({ buyer, open, onClose, onSent, zIndex }) => {
  const [q, setQ] = React.useState('');
  const [hits, setHits] = React.useState([]);
  const [pick, setPick] = React.useState(null);
  const [blast, setBlast] = React.useState(buyer.buyer_type === 'preferred_buyer' ? 'pb' : 'peg');
  const [note, setNote] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => { if (!open) { setQ(''); setHits([]); setPick(null); setNote(''); } }, [open]);
  React.useEffect(() => {
    if (!open || q.trim().length < 2) { setHits([]); return undefined; }
    let alive = true;
    const t = setTimeout(() => {
      window.AtlasApi.search(q.trim(), 12)
        .then((r) => { if (alive) setHits(((r && r.results) || r || []).filter((x) => x.entity_type === 'engagement')); })
        .catch(() => { if (alive) setHits([]); });
    }, 200);
    return () => { alive = false; clearTimeout(t); };
  }, [q, open]);
  const send = async () => {
    if (!pick) return;
    setBusy(true);
    try {
      const r = await window.AtlasApi.sendBuyerDeal(buyer.buyer_id, { engagement_id: pick.id, blast_type: blast, note: note.trim() || null });
      window.pushToast(r.added ? { type: 'success', title: `${buyer.firm} added to ${r.deal}` }
                               : { type: 'info', title: `${buyer.firm} is already on ${r.deal}` });
      onSent && onSent(r);
      onClose();
    } catch (e) {
      window.pushToast({ type: 'error', title: 'Could not add them', body: e.message });
    } finally { setBusy(false); }
  };
  const input = { width: '100%', boxSizing: 'border-box', padding: '7px 9px', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface)', color: 'var(--text)', fontSize: 12.5, font: 'inherit' };
  return (
    <window.Drawer open={open} onClose={onClose} title={`Send a deal to ${buyer.firm}`}
      width={480} zIndex={zIndex}
      footer={(
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <window.Btn variant="ghost" onClick={onClose}>Cancel</window.Btn>
          <window.Btn variant="primary" disabled={!pick || busy} onClick={send}>{busy ? 'Adding…' : 'Add to deal'}</window.Btn>
        </div>
      )}>
      <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 12, overflow: 'auto' }}>
        <div>
          <div style={bdRowLabel}>Seller deal</div>
          {pick ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, padding: '7px 9px', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface-2)' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 600 }}>{pick.title}</div>
                {pick.subtitle && <div style={{ fontSize: 11, color: 'var(--text-4)' }}>{pick.subtitle}</div>}
              </div>
              <window.Btn size="xs" variant="ghost" onClick={() => setPick(null)}>Change</window.Btn>
            </div>
          ) : (
            <>
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search deals by name or company…" style={{ ...input, marginTop: 4 }} />
              <div style={{ marginTop: 4, maxHeight: 260, overflow: 'auto' }}>
                {hits.map((h) => (
                  <button key={h.id} onClick={() => setPick(h)}
                    style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 9px', background: 'transparent', border: 'none', borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                    className="row-hover">
                    <div style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--text)' }}>{h.title}</div>
                    {h.subtitle && <div style={{ fontSize: 11, color: 'var(--text-4)' }}>{h.subtitle}</div>}
                  </button>
                ))}
                {q.trim().length >= 2 && !hits.length && <div style={{ fontSize: 11.5, color: 'var(--text-4)', padding: '6px 9px' }}>No deals match.</div>}
              </div>
            </>
          )}
        </div>
        <div>
          <div style={bdRowLabel}>Blast type</div>
          <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
            {[['pb', 'PB'], ['peg', 'PEG']].map(([v, l]) => (
              <window.Btn key={v} size="xs" variant={blast === v ? 'primary' : 'outline'} onClick={() => setBlast(v)}>{l}</window.Btn>
            ))}
          </div>
        </div>
        <div>
          <div style={bdRowLabel}>Note</div>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Why this buyer — lands in the tracker row" style={{ ...input, marginTop: 4, resize: 'vertical' }} />
        </div>
      </div>
    </window.Drawer>
  );
};

// ── The page ─────────────────────────────────────────────────────────────────
// ── Header pieces the company page mounts when the company is a roster buyer ─
// Pills next to the name: tier, agreement (with expiry), firm kind, and the
// domain-suspect warning. Same wording as the roster grid (window.BuyerLabels).
// The firm's pills as a keyed list (type, agreement, firm kind, domain suspect): the classic
// header renders them together; the v2 header (buyer_firm_v2.jsx) gives each its own slot.
const buyerHeaderPillList = (buyer) => {
  const L = window.BuyerLabels || {};
  const days = L.buyerDaysToExpiry ? L.buyerDaysToExpiry(buyer.pba_expiration_date) : null;
  const agreementHue = buyer.agreement_kind ? (L.buyerExpiryHue ? L.buyerExpiryHue(days) : 'violet') : 'slate';
  return [
    <window.Pill key="type" hue={(L.BUYER_TYPE_HUE || {})[buyer.buyer_type] || 'slate'} sz="sm">{(L.BUYER_TYPE_LABEL || {})[buyer.buyer_type] || buyer.buyer_type}</window.Pill>,
    buyer.agreement_kind && (
      <window.Pill key="agreement" hue={agreementHue} sz="sm" title={buyer.pba_expiration_date ? `Agreement ${days < 0 ? 'expired' : 'expires'} ${L.fmtBuyerDate ? L.fmtBuyerDate(buyer.pba_expiration_date) : buyer.pba_expiration_date}` : 'No expiry on file'}>
        {(L.AGREEMENT_LABEL || {})[buyer.agreement_kind] || buyer.agreement_kind}{days != null && days < 0 ? ' · expired' : days != null && days <= 90 ? ` · ${days}d left` : ''}
      </window.Pill>
    ),
    buyer.firm_kind && <window.Pill key="kind" hue={(L.FIRM_KIND_HUE || {})[buyer.firm_kind] || 'slate'} sz="sm">{(L.FIRM_KIND_LABEL || {})[buyer.firm_kind] || buyer.firm_kind}</window.Pill>,
    buyer.domain_suspect && <window.Pill key="suspect" hue="amber" sz="sm" title="The last read of this domain did not look like an investment firm">domain suspect</window.Pill>,
  ].filter(Boolean);
};
const BuyerHeaderPills = ({ buyer }) => <>{buyerHeaderPillList(buyer)}</>;

// The buyer's numbers, in place of revenue/EBITDA (a PE firm has neither).
// v2 (record-page redesign step 12): with `onPick` each tile is a button that
// opens the matching Firm workspace tab (Contacts → the People drawer), and
// `live` (the roster row's live_pursuits) is the Deals sent tile's 'N live',
// the same count as the Pursuits card. Without them the tiles are the classic
// ones, element for element.
const BD_TILE_BTN = { display: 'flex', flex: '0 0 auto', minWidth: 118, padding: 0, border: 'none', background: 'transparent',
  color: 'inherit', font: 'inherit', textAlign: 'left', cursor: 'pointer' };
const BuyerKpis = ({ buyer, contacts, onPick, live }) => {
  const L = window.BuyerLabels || {};
  const dev = window.AtlasDevice || {};
  const liveN = live != null ? live : buyer.active_deals;
  const tile = (key, node) => (onPick
    ? <button type="button" data-firm-tile={key} onClick={() => onPick(key)} style={BD_TILE_BTN}>{node}</button>
    : node);
  const head = (
    <>
      {tile('deals', <window.DKpi label="Deals sent" value={buyer.deals ?? 0} sub={liveN ? `${liveN} live` : null} />)}
      {tile('ndas', <window.DKpi label="NDAs" value={buyer.nda_signed ?? 0} />)}
      {tile('passed', <window.DKpi label="Passed" value={buyer.passed ?? 0} sub={buyer.pass_profile && buyer.pass_profile.engaged ? `engaged on ${buyer.pass_profile.engaged}` : null} />)}
      {tile('band', <window.DKpi label="EBITDA band" value={L.fmtBuyerBand ? (L.fmtBuyerBand(buyer.min_ebitda, buyer.max_ebitda) || '—') : '—'} />)}
    </>
  );
  // Phone: only the four a caller needs. Tiles come back bare on every
  // device — the company page's KPI row owns the grid (two per row on a
  // phone, wrapping on an iPad); a grid of our own here would land in one
  // of its cells.
  if (dev.phone) return head;
  const rest = (
    <>
      {tile('portfolio', <window.DKpi label="Portfolio" value={buyer.portfolio_count ?? 0} sub={buyer.portfolio_realized ? `${buyer.portfolio_realized} realized` : null} />)}
      {tile('team', <window.DKpi label="Team" value={buyer.team_count ?? 0} sub={buyer.team_count ? `${buyer.team_in_atlas || 0} in Atlas` : null} />)}
      {tile('acquisitions', <window.DKpi label="Acquisitions" value={buyer.acquisitions ?? 0} />)}
      {contacts != null && tile('contacts', <window.DKpi label="Contacts" value={contacts} />)}
    </>
  );
  return <>{head}{rest}</>;
};

// "What they have said no to" — the pass lessons, framed like the other
// middle-column cards. bare (the v2 Firm workspace's Passes tab): the lessons
// only; 'Says no to' lives in the v2 header's meta line, so it is not repeated.
const BuyerPassesPanel = ({ buyer, bare = false }) => (bare ? <window.BuyerPassesBody buyer={buyer} bare /> : (
  <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, marginBottom: 14 }}>
    <div style={{ padding: '12px 14px 8px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 6 }}>
      <window.Icon.Close size={13} color="var(--accent)" />
      <span style={{ fontSize: 13, fontWeight: 600 }}>What they have said no to</span>
      {buyer.pass_profile && buyer.pass_profile.says_no_to && (
        <span style={{ marginLeft: 8, fontSize: 11.5, color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{buyer.pass_profile.says_no_to}</span>
      )}
    </div>
    <div style={{ padding: '4px 14px 10px' }}><window.BuyerPassesBody buyer={buyer} /></div>
  </div>
));

// Right-rail stack: who to call, what they own, what they bought, what moved.
const BuyerRailPanels = ({ buyer, setCompanyId }) => {
  const Icon = window.Icon;
  return (
    <>
      <BdPanel icon={<Icon.Users size={13} color="var(--accent)" />} title="Team" count={buyer.team_count || null}
        style={{ borderBottom: '1px solid var(--border)' }}>
        <window.BuyerTeamBody buyer={buyer} />
      </BdPanel>
      <BdPanel icon={<Icon.Layers size={13} color="var(--accent)" />} title="Portfolio" count={buyer.portfolio_count || null}
        style={{ borderBottom: '1px solid var(--border)' }}>
        <window.BuyerPortfolioBody buyer={buyer} setCompanyId={setCompanyId} />
      </BdPanel>
      <div style={{ borderBottom: '1px solid var(--border)' }}><BuyerAcquisitionsPanel buyer={buyer} setCompanyId={setCompanyId} /></div>
      <div style={{ borderBottom: '1px solid var(--border)' }}><BuyerSignalsPanel buyer={buyer} /></div>
      <div style={{ borderBottom: '1px solid var(--border)' }}><BuyerChangesPanel buyer={buyer} /></div>
    </>
  );
};

// ── Old links: #/buyers/<buyer_id> ───────────────────────────────────────────
// The buyer page lived at its own route for one release (202609231400). It is
// now part of the company page, so a saved link, an Ask Atlas answer or a
// cmd-click from the roster lands here for a moment, resolves the firm's
// company and moves on. The URL is REPLACED before the company view mounts,
// so the back button skips this stop instead of bouncing between the two.
const BuyerRedirect = ({ buyerId, setCompanyId, back }) => {
  const [failed, setFailed] = React.useState(null);
  React.useEffect(() => {
    let alive = true;
    setFailed(null);
    window.AtlasApi.getBuyer({ buyer_id: buyerId })
      .then((b) => {
        if (!alive) return;
        if (!b || !b.company_id) { setFailed('This buyer has no company record.'); return; }
        try { history.replaceState(null, '', '#/companies/' + encodeURIComponent(b.company_id)); } catch (_) { /* fall through */ }
        setCompanyId(b.company_id);
      })
      .catch((e) => { if (alive) setFailed(e.message || 'Could not load this buyer'); });
    return () => { alive = false; };
  }, [buyerId]);  // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div style={{ padding: 24 }}>
      <button onClick={back} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-3)', padding: 0, fontSize: 11.5 }}>‹ Buyers</button>
      <div style={{ marginTop: 12, fontSize: 12.5, color: 'var(--text-3)' }}>{failed || 'Opening the firm…'}</div>
    </div>
  );
};

window.BuyerRedirect = BuyerRedirect;
// The company page assembles these when the company is on the buyer roster.
window.BuyerFirm = {
  HeaderPills: BuyerHeaderPills,
  Kpis: BuyerKpis,
  ProfilePanel: BuyerProfilePanel,
  DealsPanel: BuyerDealsPanel,
  PassesPanel: BuyerPassesPanel,
  RailPanels: BuyerRailPanels,
  SendDealDrawer,
  // Record-page redesign step 12: the Firm workspace tabs and the v2 Investment profile's options.
  AcquisitionsPanel: BuyerAcquisitionsPanel,
  SignalsPanel: BuyerSignalsPanel,
  ChangesPanel: BuyerChangesPanel,
  TYPE_OPTIONS: BUYER_TYPE_OPTIONS,
  AGREEMENT_OPTIONS,
  // …and what the v2 header and Pursuits card share with the classic panels (no copies).
  headerPillList: buyerHeaderPillList,
  dealStatus: bdDealStatus,
  NDA_HUE: BD_NDA_HUE,
  INTEREST_HUE: BD_INTEREST_HUE,
};
